import { useMemo, useState } from 'react'

import { Snackbar, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { ConfirmationDialog } from '@scoir/modals'

import {
  useCopyRounds,
  useFocusedApplicationCycle,
  useRounds,
} from 'routes/application/AppCycleRounds/AppRoundProvider.tsx'
import { useAppCycleYearRange } from 'routes/application/AppCycleRounds/AppRoundSection/app-round-helper.ts'
import { useRequirements } from 'routes/application/AppCycleRounds/RoundRequirementsProvider.tsx'
import { useAppCycle, useRoundConfigurationAppCycle } from 'store/application-cycle/hooks.ts'
import { useCanManageRoundsAndRequirements } from 'store/user/hooks'

const useStyles = makeStyles(() => ({
  underlineLink: {
    textDecoration: 'underline',
  },
}))

const CopyCurrentRoundsAlert = () => {
  const classes = useStyles()
  const copyRounds = useCopyRounds()
  const appCycle = useFocusedApplicationCycle()

  const currentAppCycle = useAppCycle()
  const currentAppCycleRange = useAppCycleYearRange(currentAppCycle)
  const upcomingCycle = useRoundConfigurationAppCycle()
  const upcomingCycleRange = useAppCycleYearRange(upcomingCycle)

  const deadlines = useRounds()
  const requirements = useRequirements()
  const canManage = useCanManageRoundsAndRequirements()
  const showCopyRoundsAlert = useMemo(
    () => deadlines.length === 0 && requirements.length === 0 && appCycle !== currentAppCycle,
    [deadlines, requirements, appCycle, currentAppCycle],
  )

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [displayError, setDisplayError] = useState<boolean>(false)

  const handleSubmit = () => {
    copyRounds(currentAppCycle)
      .catch(() => {
        setDisplayError(true)
      })
      .finally(() => setIsDialogOpen(false))
  }

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }

    setDisplayError(false)
  }

  if (!showCopyRoundsAlert || !canManage) {
    return null
  }

  return (
    <>
      <Alert
        severity="info"
        action={
          <a
            href="#"
            role="button"
            className={classes.underlineLink}
            onClick={() => setIsDialogOpen(true)}
          >
            Copy Current Rounds
          </a>
        }
      >
        No need to start from scratch. You can copy your current rounds, and then make edits if needed.
      </Alert>
      <ConfirmationDialog
        open={isDialogOpen}
        headerText="Confirm Copy Rounds"
        onConfirm={handleSubmit}
        onClose={() => setIsDialogOpen(false)}
      >
        <Typography>
          Are you sure you want to copy rounds from the {currentAppCycleRange} application cycle to the{' '}
          {upcomingCycleRange} application cycle?
        </Typography>
      </ConfirmationDialog>
      <Snackbar
        open={displayError}
        autoHideDuration={6000}
        onClose={handleClose}
        message="There was a problem copying rounds."
      />
    </>
  )
}

export default CopyCurrentRoundsAlert
